import { IStatusConfig } from '../../../components/StatusBadge'
import {
  IEventCommissionBeneficiary,
  IEventCommissionSummaryFilter,
  IEventCommissionSummaryItem
} from '../../../services/EventCommissions/interfaces'
import { commissionTypeLabels } from '../EventCoupons/couponLabels'

/** Uma venda cujo cupom não comissiona ninguém não tem vendedor a exibir. */
export const UNATTRIBUTED_LABEL = 'Vendedor não identificado'

export function sellerDisplayName(row: IEventCommissionBeneficiary): string {
  return row.seller_name ?? UNATTRIBUTED_LABEL
}

export function beneficiaryLabel(row: IEventCommissionBeneficiary): string {
  // Na linha sem beneficiário o subtítulo é a única pista do que ela agrega: venda paga cujo
  // cupom ninguém configurou — não uma pessoa que o sistema não conseguiu identificar.
  return row.commission_type ? commissionTypeLabels[row.commission_type] : 'Cupom sem comissionamento configurado'
}

/**
 * Filtros que escopam a listagem de vendas a exatamente esta linha da comissão.
 *
 * Parceiros não têm cadastro, então são identificados pelo nome; os demais, pelo id do
 * professor ou do consultor. Sem tipo, 'none' pega as vendas que não comissionam ninguém.
 */
export function beneficiaryScope(
  row: IEventCommissionBeneficiary
): Pick<IEventCommissionSummaryFilter, 'commission_type' | 'commission_target_id' | 'partner_name'> {
  if (row.commission_type === null) return { commission_type: 'none' }
  if (row.commission_type === 'partner') {
    return { commission_type: row.commission_type, partner_name: row.partner_name ?? '' }
  }

  return { commission_type: row.commission_type, commission_target_id: row.commission_target_id ?? '' }
}

/**
 * Chave estável de linha: o id só é único dentro do seu tipo de beneficiário.
 *
 * Parceiro sem nome não duplica chave: o backend agrupa todos eles num balde único
 * (`EventCommissionController::summaryKey()` → `partner:`), então nunca chega mais de uma
 * linha dessas.
 */
export function beneficiaryKey(row: IEventCommissionBeneficiary): string {
  if (row.commission_type === null) return 'unattributed'
  if (row.commission_type === 'partner') return `partner:${row.partner_name ?? ''}`

  return `${row.commission_type}:${row.commission_target_id}`
}

/**
 * Um parceiro é identificado pelo nome digitado, não por cadastro. Sem nome não há como
 * escopar as vendas: `appendBeneficiary()` descarta o `partner_name` vazio e o modal abriria
 * listando as vendas de todos os parceiros. Nesses casos o botão fica travado.
 */
export function canOpenSales(row: IEventCommissionBeneficiary): boolean {
  return row.commission_type !== 'partner' || !!row.partner_name?.trim()
}

/**
 * Situação de pagamento da linha, derivada das contagens do resumo.
 *
 * 'none' é a linha sem comissionamento e a que só tem venda de R$ 0,00: não há o que pagar,
 * então nem "pendente" ela é. 'partial' é quem recebeu por parte das vendas — o dinheiro saiu,
 * mas a linha ainda deve.
 */
export type CommissionPaymentStatus = 'paid' | 'partial' | 'pending' | 'none'

export function paymentStatus(row: IEventCommissionSummaryItem): CommissionPaymentStatus {
  if (row.paid_sales_count === 0 && row.pending_sales_count === 0) return 'none'
  if (row.pending_sales_count === 0) return 'paid'

  return row.paid_sales_count > 0 ? 'partial' : 'pending'
}

/** Local: é um conjunto só deste módulo. Rótulo e cor juntos, como manda o `StatusBadge`. */
export const COMMISSION_PAYMENT_STATUS: Record<CommissionPaymentStatus, IStatusConfig> = {
  paid: { label: 'Pago', colorScheme: 'green' },
  partial: { label: 'Parcial', colorScheme: 'yellow' },
  pending: { label: 'Pendente', colorScheme: 'gray' },
  none: { label: '—', colorScheme: 'gray' }
}
